import { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { HOST_MB } from '@/lib/constants';
import { logout } from '@/hooks/auth/use-auth';
import { useContractsData, useSelectedContract } from '@/hooks/api/use-contracts';
import { useReceiptsUnpaid } from '@/hooks/api/use-receipts';
import { useIsFleetUser } from '@/hooks/api/use-user';
import useBrandName from '@/hooks/use-brand-name';
import { hasContractApporteur43397, hasContractVAEUnder14Days as hasVAEUnder14Days, hasOtherContract as hasOther, isWakamContract } from '@/lib/contracts';
import { ALL_PRODUCT_ICONS } from '@/lib/product-icon';
import amGestionLogo from '@/assets/images/am-gestion.png';

interface NavItem {
  to: string;
  label: string;
  always?: boolean;
  show?: boolean;
  badge?: number;
}

const Header = () => {
  const contracts = useContractsData();
  const isWakam = isWakamContract(useSelectedContract());
  const hasApporteur43397 = hasContractApporteur43397(contracts);
  const hasContractVAEUnder14Days = hasVAEUnder14Days(contracts);
  const hasOtherContract = hasOther(contracts);
  const isFleetUser = useIsFleetUser();
  // Charge les quittances impayées pour afficher la notification sur "Paiements"
  const receipts = useReceiptsUnpaid(!isFleetUser);
  const postLogout = () => logout();
  const unpaidReceiptsCount = receipts.data?.data?.length || 0;
  const logo = HOST_MB === 43397 ? '/images/logo.svg' : amGestionLogo;
  const brandName = useBrandName();
  // Pour les clients sans contrat 43397, on affiche tous les pictos produits
  // au lieu du nom de la marque.
  const showProductIcons = HOST_MB !== 43397 && !hasApporteur43397;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Handle scroll for sticky header effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  const pathname = window.location.pathname;
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const tutorialIdMap: Record<string, string> = {
    '/mes-contrats': 'nav-contrats',
    '/mes-paiements': 'nav-paiements',
    '/mes-questions': 'nav-questions',
    '/mes-avantages': 'nav-avantages',
    '/mes-documents': 'nav-documents',
    '/mes-parrainages': 'nav-parrainages',
  };

  const navItems: NavItem[] = isFleetUser
      ? [
            { to: '/mes-contrats', label: 'Contrats', always: true },
            { to: '/mes-sinistres', label: 'Sinistres', always: true },
        ]
      : [
            { to: '/', label: 'Accueil', always: true },
            { to: '/mes-contrats', label: 'Contrats', always: true },
            { to: '/mes-paiements', label: 'Paiements', always: true, badge: unpaidReceiptsCount },
            { to: '/mes-sinistres', label: 'Sinistres', always: true },
            { to: '/mes-questions', label: 'Questions', always: true },
            { to: '/mes-avantages', label: 'Avantages', show: !isWakam && hasOtherContract },
            { to: '/mes-documents', label: 'Documents', show: contracts?.[0]?.vehicule?.codeProduit !== 'WAKAM_LIV' },
            {
                to: '/mes-parrainages',
                label: 'Parrainages',
                show: HOST_MB === 43397 && hasApporteur43397 && hasOtherContract,
            },
            {
                to: '/resilier',
                label: hasContractVAEUnder14Days ? 'Renoncer mon contrat' : 'Résilier',
                show: HOST_MB === 43397 && hasApporteur43397,
            },
        ].filter((item) => item.always || item.show);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full transition-all duration-300',
        'bg-card/60 backdrop-blur-md border-b border-border',
        isScrolled && 'shadow-md bg-card/75'
      )}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="relative flex h-16 items-center">
        {/* Logo / Nom apporteur - collé à gauche du viewport */}
        <Link to="/" aria-label={`Espace client ${brandName} - Accueil`} className="flex items-center gap-3 flex-shrink-0 no-underline hover:opacity-80 transition-opacity pl-3 sm:pl-6 z-10">
          {showProductIcons ? (
            <span className="flex items-center gap-1.5 sm:gap-2">
              {ALL_PRODUCT_ICONS.map((Icon, index) => (
                <Icon key={index} className="h-6 w-6 sm:h-8 sm:w-8 text-black shrink-0" aria-hidden="true" />
              ))}
            </span>
          ) : logo ? (
            <img src={logo} alt="" aria-hidden="true" className="h-14 w-auto" />
          ) : (
            <span className="text-2xl" aria-hidden="true">🏍️</span>
          )}
        </Link>

        {/* Desktop Navigation - centré sur la page */}
        <nav data-tutorial="header-nav" className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2" aria-label="Navigation principale">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              {...(tutorialIdMap[item.to] ? { 'data-tutorial': tutorialIdMap[item.to] } : {})}
              className={({ isActive }) =>
                cn(
                  'relative px-4 py-2 rounded-md text-[0.875rem] font-medium transition-colors no-underline whitespace-nowrap',
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              {item.label}
              {!!item.badge && item.badge > 0 && (
                <span
                  className="absolute -top-0.5 right-0 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[0.625rem] font-semibold leading-none"
                  aria-label={`${item.badge} cotisation${item.badge > 1 ? 's' : ''} à payer`}
                >
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Profile & Logout - aligné avec le contenu de la page */}
        <div className="hidden lg:flex items-center justify-end absolute inset-0 max-w-7xl mx-auto px-4 pointer-events-none">
          <div className="flex items-center gap-1 pointer-events-auto">
            <Link
              to="/mon-profil"
              data-tutorial="nav-profil"
              className={cn(
                'p-2 rounded-full bg-[#0069bd] text-white no-underline transition-all duration-200',
                'hover:bg-primary-hover hover:shadow-md'
              )}
              aria-label="Mon profil"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </Link>
            <button
              onClick={() => { postLogout(); }}
              className={cn(
                'p-2 rounded-lg transition-colors cursor-pointer',
                'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
              )}
              aria-label="Se déconnecter"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              'lg:hidden ml-auto p-2 rounded-lg transition-colors',
              'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
            aria-label="Menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
      </div>

      {/* Mobile Navigation */}
      <div
        id="mobile-nav"
        aria-hidden={!isMobileMenuOpen}
        /* Valeur d'origine conservée : React 19 traite `inert=""` comme faux (attribut non posé). */
        {...(!isMobileMenuOpen ? { inert: '' as unknown as boolean } : {})}
        className={cn(
          'lg:hidden overflow-hidden transition-all duration-300 ease-in-out',
          isMobileMenuOpen ? 'max-h-[70vh] border-t border-border' : 'max-h-0'
        )}
      >
        <nav className="px-6 py-4 space-y-1 bg-card overflow-y-auto max-h-[calc(70vh-2px)]" aria-label="Navigation mobile">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-between px-6 py-4 rounded-md text-[0.875rem] font-medium transition-colors no-underline',
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <span>{item.label}</span>
              {!!item.badge && item.badge > 0 && (
                <span
                  className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-semibold"
                  aria-label={`${item.badge} cotisation${item.badge > 1 ? 's' : ''} à payer`}
                >
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}

          <div className="border-t border-border my-2" />

          <NavLink
            to="/mon-profil"
            onClick={() => setIsMobileMenuOpen(false)}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-6 py-4 rounded-md text-[0.875rem] font-medium transition-colors no-underline text-[#0069bd]',
                isActive
                  ? 'bg-[#0069bd]/10'
                  : 'hover:bg-[#0069bd]/10'
              )
            }
          >
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Mon profil
          </NavLink>

          <button
            onClick={() => { setIsMobileMenuOpen(false); postLogout(); }}
            className="flex items-center gap-3 w-full px-6 py-4 rounded-md text-[0.875rem] font-medium transition-colors text-destructive hover:bg-destructive/10 cursor-pointer"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Se déconnecter
          </button>
        </nav>
      </div>

    </header>
  );
};

export default Header;
